import React, { useContext } from 'react';
import Helmet from 'react-helmet';
import { BREAKPOINT, COLOR } from '@latitude/core/utils/constants';
import {
  AnalyticsLocationProvider,
  AnalyticsContext
} from 'latitude-analytics';
import Layout from '@/components/layout';
import { ImportantInformation } from '@latitude/important-information';
import { PageContext } from '@/context/PageContext';
import HeroBanner from '@/components/lab-components/HeroBanner';
import GemMicrositeHeader from '../_gem-microsite-header';
import pageDataBackup from '@/data/pages/credit-cards/gem-visa-rewards.json';
import HeroBranded from '@/components/HeroBranded/HeroBranded';
import StandaloneText from '@/components/lab-components/StandaloneText';
import ImageBanner from '@/components/lab-components/ImageBanner';
import {
  AppStoreBadge,
  AppStoreBadgeContainer
} from '@/components/AppStoreBadge/AppStoreBadge';
import CardFeature from '@/components/lab-components/CardFeature';
import ImageGrid from '@/components/lab-components/ImageGrid';
import imgHero from '../../../mobile/images/mobile-app-hero.webp';
import tick from './tick.png';
import imageBannerDesktop from './latitude-reward-image-banner-desktop.png';
import imageBannerMobile from './latitude-reward-image-banner-mobile.png';
import gemCardArt from './gem-card-art.png';
import appleImg from './apple.png';
import pricelineImg from './priceline.png';
import woolworthsImg from './woolies.png';
import goodGuysImg from './good-guys.png';
import jbHiFiImg from './jbhifi.png';
import doordashImg from './doordash.png';
import bestRestImg from './best-restaurants.png';
import hnImage from './harvey-norman.png';
import ikeaImg from './ikea.png';
import ebayImg from './ebay.png';
import FeatureGrid from '@/components/lab-components/FeatureGrid';
import CardCollection from '@/components/lab-components/CardCollection';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import { disclaimer } from '@/selectors/car-loan';

function GemRewardsPage(props) {
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || pageDataBackup;
  const [analytics] = useContext(AnalyticsContext);

  // adding pageData to analytics
  analytics.pageData = {
    page_pageTitle: 'latitude-rewards',
    page_pageType: 'information-page',
    page_siteSection: 'cards',
    page_siteSubsection: 'gem-visa',
    product_productid: ['CCAUGEM']
  };

  const imageBannerData = {
    desktopImage: imageBannerDesktop,
    mobileImage: imageBannerMobile,
    imageAltText: 'latitude-reward-image-banner'
  };

  const cardFeatureData = {
    title: 'Ready for what’s next?',
    image: gemCardArt,
    reward:'Find out how to earn $400 Latitude Rewards',
    cardName: 'Latitude Gem Visa',
    description:'On eligible spend including Interest Free purchases. Minimum spend applies.',
    ctaText: 'Learn more',
    ctaLink: 'https://www.latitudefinancial.com.au/credit-cards/gem-visa/',
    disclaimer: '',
    altText: 'latitude-gem-visa-card'
  };

  const imageGridData = {
    maxCols: 5,
    title: 'Choose from over 100 eGift cards to spend your Latitude Rewards on',
    images: [
      { src: appleImg, alt: 'apple' },
      { src: woolworthsImg, alt: 'woolies' },
      { src: pricelineImg, alt: 'priceline' },
      { src: hnImage, alt: 'hn' },
      { src: goodGuysImg, alt: 'good-guys' },
      { src: ikeaImg, alt: 'ikea' },
      { src: ebayImg, alt: 'ebay' },
      { src: jbHiFiImg, alt: 'jb-hifi' },
      { src: doordashImg, alt: 'doordash' },
      { src: bestRestImg, alt: 'best-restaurants' }
    ]
  };

  const featureGridData = {
    maxCols: 4,
    title: 'There are 4 ways to earn Latitude Rewards',
    description:
      'Earn 3% back in Latitude Rewards<sup>1</sup> on eligible spend across groceries, dining, food delivery, movie and theatre ticket purchases — after you spend $1,000 or more on general purchases in a statement period. <a href="https://assets.latitudefinancial.com/legals/terms-conditions/gemvisa-rewards.pdf" target="_blank">T&Cs apply.</a>',
    features: [
      { icon: 'icon2-grocery-shopping-blue', text: 'Grocery shopping' },
      { icon: 'icon2-dining-out-blue', text: 'Dining out' },
      { icon: 'icon2-food-delivery-blue', text: 'Food delivery' },
      { icon: 'icon2-cinema-ticket-blue', text: 'Cinema & theatre tickets' }
    ]
  };

  return (
    <Layout location={props.location} hasSecNav>
      <main
        className="navigation-spacer"
        css={`
          margin-top: 130px !important;
          .microsite-header {
            z-index: 2000 !important;
          }

          div[class^='MicrositeHeader__SecondBar'].microsite-header__nav-wrap {
            margin-top: 8px;
          }

          @media (max-width: ${BREAKPOINT.SM}) {
            margin-top: 105px !important;
          }
          h5 {
            margin-top: 0px;
          }
          @media (min-width: 992px) and (max-width: 1200px) {
            div[class^='MicrositeHeader__SecondBar-']
              a[class*='MicrositeHeader__ProductTitle-'] {
              white-space: nowrap;
            }
          }
          div.card-wrapper a {
            display: flex;
            flex-direction: column-reverse;
          }
        `}
      >
        <Helmet>
          <link
            rel="canonical"
            href="https://www.latitudefinancial.com.au/credit-cards/gem-visa/latitude-rewards"
            hreflang="x-default"
          />
          <title>
            Gem Visa Rewards | Earn with Latitude Rewards
          </title>
          <meta
            name="description"
            content="Make the most of your Gem Visa with Latitude Rewards. Learn how to earn points, access offers, and enjoy more value."
          />
        </Helmet>
        <GemMicrositeHeader data={state?.secondaryNavData} />
        <HeroV2 {...state?.heroBannerData?.[0] || pageDataBackup?.heroBannerData?.[0]} />
        <FeatureGrid {...featureGridData} />
        <ImageBanner {...imageBannerData} />
        <ImageGrid {...imageGridData} />
        {state?.cardCollectionData?.[0] && (
          <CardCollection
            id="card-collection"
            data={state?.cardCollectionData?.[0]}
          />
        )}
        <div
          css={`
            && li::marker {
              content: url(${tick});
              color: #0555c8;
            }
            p {
              bottom: 3px;
              position: relative;
              padding-left: 10px;
            }
            ul {
              padding-left: 20px;
              list-style-image: url(${tick});
            }
          `}
        >
          <StandaloneText {...pageDataBackup?.standaloneTextData?.[1]} />
        </div>

        <CardFeature {...cardFeatureData} />

        <HeroBranded
          fastApply
          className="hero-control mobile-hero"
          css={`
            background: linear-gradient(128.6deg, #0146aa, #0162ed);
            @media (max-width: ${BREAKPOINT.MD}) {
              .HeroContent {
                background-color: transparent;
              }
            }
            h1 {
              margin-bottom: 0px !important;
            }
          `}
          imageContent={
            <div
              css={`
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                @media (min-width: ${BREAKPOINT.LG}) {
                  justify-content: flex-end;
                }
              `}
            >
              <img
                src={imgHero}
                alt=""
                css={`
                  height: 313px;
                  width: auto;
                  padding: 40px 0;
                  position: absolute;
                  top: 0;
                  @media (min-width: ${BREAKPOINT.MD}) {
                    height: 370px;
                    padding: 32px 0;
                  }
                `}
              />
            </div>
          }
          title={
            <div
              css={`
                color: white;
                font-size: 32px;
                line-height: 36px;
              `}
            >
              Get the Latitude App.
            </div>
          }
          text={
            <div
              css={`
                padding: 8px 0 0;
              `}
            >
              <div
                css={`
                  margin-bottom: 24px;
                  color: white;
                `}
              >
                Manage your account and make repayments in
                just a few taps with our mobile app.
              </div>
              <div
                css={`
                  color: white;
                  margin: 12px 0;
                `}
              >
                Download now!
              </div>
              <AnalyticsLocationProvider location="hero">
                <AppStoreBadgeContainer>
                  <AppStoreBadge url="https://apps.apple.com/app/apple-store/id1373059866" />
                  <AppStoreBadge url="https://play.google.com/store/apps/details?id=com.latitudefinancial.latitudeapp" />
                </AppStoreBadgeContainer>
              </AnalyticsLocationProvider>
            </div>
          }
        />

        <ImportantInformation
          data={{
            content: {
              importantInformationSectionOne:
                pageDataBackup?.importantInformationSectionOne,
              importantInformationSectionTwo:
                pageDataBackup?.importantInformationSectionTwo
            }
          }}
          sectionOneColummClasses="col-10 offset-1 col-lg-5 offset-lg-1"
          sectionTwoColummClasses="col-10 offset-1 col-lg-5 offset-lg-0"
          css={`
            && {
              background-color: ${COLOR.GREY10};
            }
            li {
              margin-left: 15px;
            }
          `}
        />
      </main>
    </Layout>
  );
}

export default GemRewardsPage;
